import { Link } from 'react-router-dom';
import { featuredComics } from '@/mocks/comics';

export default function ComicHero({ slug }: { slug: string }) {
  const comic = featuredComics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <section className="relative w-full h-[420px] md:h-[560px] flex items-center justify-center bg-foreground-900">
        <div className="text-center text-background-50">
          <h2 className="text-2xl font-bold font-heading">漫画未找到</h2>
        </div>
      </section>
    );
  }

  return (
    <section className="relative w-full h-[500px] md:h-[640px] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={comic.coverImage}
          alt={`${comic.title} - 虫虫漫画网完整版免费阅读`}
          className="w-full h-full object-cover object-top blur-sm scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/50 to-foreground-950/80"></div>
      </div>

      <div className="relative z-10 w-full max-w-4xl mx-auto px-6 flex flex-col items-center text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-500/20 backdrop-blur-sm border border-accent-400/30 mb-5">
          <span className="text-accent-300 text-xs font-medium">
            {comic.status === '已完结' ? '已完结 · 免费阅读' : '连载中 · 持续更新'}
          </span>
        </div>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-background-50 mb-3">
          {comic.title}
        </h1>
        <div className="flex items-center gap-4 text-sm text-background-50/70 mb-4">
          <span>作者：{comic.author}</span>
          <span className="text-background-50/30">|</span>
          <span>共 {comic.chapters} 话</span>
          <span className="text-background-50/30">|</span>
          <span className="flex items-center gap-1">
            <i className="ri-star-fill text-accent-400 text-sm"></i>
            {comic.rating} 分
          </span>
          <span className="text-background-50/30">|</span>
          <span>{comic.views.toLocaleString()} 次阅读</span>
        </div>
        <p className="text-sm md:text-base text-background-50/75 max-w-2xl leading-relaxed mb-8">
          {comic.summary}
        </p>
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <a
            href="#start-reading"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 text-sm md:text-base font-medium rounded-lg hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-book-open-line text-lg"></i>
            开始下拉阅读
          </a>
          <button
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50/15 backdrop-blur-sm text-background-50 text-sm md:text-base font-medium rounded-lg border border-background-50/25 hover:bg-background-50/25 transition-all duration-300 whitespace-nowrap cursor-pointer"
            type="button"
          >
            <i className="ri-heart-line text-lg"></i>
            加入收藏
          </button>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="absolute bottom-6 left-0 right-0 z-10">
        <div className="w-full px-6 md:px-10 max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-xs text-background-50/60" aria-label="面包屑导航">
            <Link to="/" className="hover:text-background-50 transition-colors duration-200 whitespace-nowrap cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link
              to={`/category/${comic.category}`}
              className="hover:text-background-50 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              漫画分类
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-background-50/90">{comic.title}</span>
          </nav>
        </div>
      </div>
    </section>
  );
}

export function ComicDetail({ slug }: { slug: string }) {
  const comic = featuredComics.find((c) => c.slug === slug);

  if (!comic) return null;

  return (
    <section className="w-full py-16 md:py-24 bg-gradient-to-b from-secondary-100 via-secondary-50/50 to-background-50">
      <div className="w-full px-6 md:px-10 max-w-7xl mx-auto">
        <div className="mb-10">
          <span className="inline-flex items-center gap-2 px-3 py-1 text-xs font-medium text-primary-700 bg-primary-100 rounded-full mb-4 font-label">
            <span className="w-1 h-1 rounded-full bg-primary-500"></span>
            漫画详情
          </span>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-950 mb-3">
            剧情介绍
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
            {comic.summary}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Cover */}
          <div className="flex justify-center">
            <div className="w-48 md:w-56 aspect-[3/4] rounded-xl overflow-hidden bg-background-100">
              <img
                src={comic.coverImage}
                alt={`${comic.title} 漫画封面`}
                title={`${comic.title} - 虫虫漫画网`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Stats */}
          <div>
            <div className="text-6xl md:text-7xl lg:text-8xl font-bold font-heading text-foreground-950 mb-3">
              {comic.chapters}
              <span className="text-2xl md:text-3xl text-foreground-400 font-normal ml-2">话</span>
            </div>
            <p className="text-sm text-foreground-500 leading-relaxed mb-2">
              总章节数 · 下拉式完整版免费阅读
            </p>
            <p className="text-sm text-foreground-500 leading-relaxed mb-6">
              评分 {comic.rating} / 累计阅读 {comic.views.toLocaleString()} 次
            </p>

            <div className="flex flex-wrap gap-3 mb-8">
              {comic.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full">
                  {tag}
                </span>
              ))}
              <span className="px-3 py-1 text-xs bg-accent-100 text-accent-800 rounded-full">
                {comic.status}
              </span>
            </div>

            <a
              href="#start-reading"
              className="inline-flex items-center gap-2 px-7 py-3 bg-foreground-950 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              立即阅读
              <i className="ri-arrow-right-line text-base"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export function RelatedComics({ slug }: { slug: string }) {
  const related = featuredComics
    .filter((c) => c.slug !== slug)
    .slice(0, 3);

  return (
    <section className="w-full py-16 md:py-24 bg-background-100">
      <div className="w-full px-6 md:px-10 max-w-7xl mx-auto">
        <div className="mb-10">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-950">
            相关推荐
          </h2>
          <p className="text-sm text-foreground-500 mt-2">
            发现更多精彩韩国漫画 · 下拉式完整版免费阅读
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {related.map((comic) => (
            <Link
              key={comic.slug}
              to={`/comic/${comic.slug}`}
              className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
            >
              <div className="relative aspect-[3/4] overflow-hidden">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title} - 虫虫漫画网`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-foreground-950/60 backdrop-blur-sm rounded-full">
                  <i className="ri-star-fill text-accent-400 text-xs"></i>
                  <span className="text-xs text-background-50 font-medium">{comic.rating}</span>
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-base font-bold font-heading text-foreground-900 group-hover:text-primary-600 transition-colors duration-200">
                  {comic.title}
                </h3>
                <div className="flex items-center gap-3 text-xs text-foreground-400 mt-1.5">
                  <span>{comic.author}</span>
                  <span className="text-foreground-200">·</span>
                  <span>{comic.chapters}话</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}